import React from 'react'
import PropTypes from 'prop-types'
import { Step, Typography, Stepper, StepLabel, Grid, Divider } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import SaveIcon from '@material-ui/icons/Save'
import moment from 'moment'
import localization from 'moment/locale/pt-br'

import { CustomButton, ModalConfirmacao } from 'components'
import { getLabelGrauRelacaoCadaver } from 'helpers/labels'
import { FormCadaver } from './FormCadaver'
import { FormPessoaReconhecimento } from './FormPessoaReconhecimento'
import { FormArquivos } from './FormArquivos'
import { useFormReconhecimento } from './hooks/useFormReconhecimento'
import useStyles from './Form.styles'

moment.locale('pt-br', localization)

export const Form = (props) => {
  const { cadaver } = props
  const classes = useStyles()

  const {
    activeStep,
    steps,
    cadaverForm,
    pessoaForm,
    processando,
    openEspelho,
    setOpenEspelho,
    dadosFormatados,
    formError,
    handleBack,
    handleNext,
    handleConfirmar,
    router,
  } = useFormReconhecimento({ cadaver })

  const getStepTitle = () => {
    switch (activeStep) {
      case 0:
        return 'DADOS DO CADÁVER'
      case 1:
        return 'DADOS DO RECONHECEDOR'
      case 2:
        return 'ARQUIVOS RELACIONADOS'
      default:
        return ''
    }
  }

  const getFormContent = () => {
    switch (activeStep) {
      case 0:
        return <FormCadaver cadaver={cadaver} cadaverForm={cadaverForm} />
      case 1:
        return <FormPessoaReconhecimento isReconhecedor pessoaForm={pessoaForm} />
      case 2:
        return <FormArquivos form={pessoaForm} processando={processando} />
      default:
        return null
    }
  }

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Grid container justifyContent="space-between" spacing={3} className={classes.contentSplitter}>
        <Grid item md={12} sm={12} xs={12} className={classes.downContainer}>
          <Grid container spacing={2}>
            <Grid item md={12} sm={12} xs={12}>
              <Typography component="h5" variant="h6">
                {getStepTitle()}
              </Typography>
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <Divider />
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              {getFormContent()}
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <Divider />
            </Grid>


            <Grid item md={12} sm={12} xs={12}>
              <div className={classes.botoesFormulario}>
                <div className={classes.botaoContainer}>
                  {activeStep === 0 ? (
                    <CustomButton
                      variant="outlined"
                      colorType="primary"
                      onClick={() => router.history.goBack()}
                      startIcon={<ArrowBackIcon />}
                      disabled={processando}
                    >
                      Voltar
                    </CustomButton>
                  ) : (
                    <CustomButton
                      variant="contained"
                      colorType="primary"
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon />}
                      disabled={processando}
                    >
                      Etapa Anterior
                    </CustomButton>
                  )}
                </div>
                <div className={classes.botoesDireita}>
                  <div className={classes.botaoContainer}>
                    <CustomButton
                      colorType="secondary"
                      variant="contained"
                      disabled={formError() || processando}
                      isLoading={processando}
                      onClick={handleNext}
                      endIcon={activeStep === steps.length - 1 ? <SaveIcon /> : <ArrowForwardIcon />}
                    >
                      {activeStep === steps.length - 1 ? 'Confirmar Reconhecimento' : 'Próxima Etapa'}
                    </CustomButton>
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Modal de Espelhamento de Dados do Reconhecimento */}
      {dadosFormatados.cadaver && (
        <ModalConfirmacao
          aberto={openEspelho}
          onClose={() => {
            if (!processando) {
              setOpenEspelho(false)
            }
          }}
          onConfirm={handleConfirmar}
          processando={processando}
          titulo="Confirmar Reconhecimento"
          confirmarLabel="Confirmar"
          cancelarLabel="Voltar"
          confirmarColor="secondary"
          maxWidth="md"
        >
          <Grid container spacing={2} className={classes.dadosReconhecimento}>
            <Grid item xs={12}>
              <Typography className={classes.titleCampo}>
                DADOS DO CADÁVER
              </Typography>
              <Divider className={classes.dividerTitle} />
            </Grid>

            <Grid item md={4} sm={4} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Nome</b>
              </Typography>
              <Typography variant="body1">{dadosFormatados.cadaver.nome}</Typography>
            </Grid>

            <Grid item md={4} sm={4} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Sexo</b>
              </Typography>
              <Typography variant="body1">{dadosFormatados.cadaver.sexo}</Typography>
            </Grid>

            <Grid item md={4} sm={4} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Filiações:</b>
              </Typography>
              {dadosFormatados.cadaver.filiacoes && dadosFormatados.cadaver.filiacoes.length > 0 ? (
                dadosFormatados.cadaver.filiacoes.map((filiacao, index) => (
                  <Typography key={index} variant="body1">
                    {filiacao}
                  </Typography>
                ))
              ) : (
                <Typography variant="body1">NÃO INFORMADO</Typography>
              )}
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Naturalidade:</b>
              </Typography>
              <Typography variant="body1">{dadosFormatados.cadaver.naturalidade || 'NÃO INFORMADO'}</Typography>
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Data de nascimento:</b>
              </Typography>
              <Typography variant="body1">
                {dadosFormatados.cadaver.dataNascimento
                  ? moment(dadosFormatados.cadaver.dataNascimento).format('DD/MM/YYYY')
                  : 'NÃO INFORMADO'}
              </Typography>
            </Grid>

            <Grid item xs={12} style={{ marginTop: '16px' }}>
              <Typography className={classes.titleCampo}>
                DADOS DO RESPONSÁVEL PELO RECONHECIMENTO
              </Typography>
              <Divider className={classes.dividerTitle} />
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Nome:</b>
              </Typography>
              <Typography variant="body1">{dadosFormatados.reconhecedor.nome}</Typography>
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Documento:</b>
              </Typography>
              <Typography variant="body1">
                {dadosFormatados.reconhecedor.tipoDocumento} {dadosFormatados.reconhecedor.numeroDocumento}{' '}
                {dadosFormatados.reconhecedor.orgaoEmissorDocumento}
              </Typography>
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Telefone:</b>
              </Typography>
              <Typography variant="body1">{dadosFormatados.reconhecedor.telefone}</Typography>
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <Typography variant="body2" color="textSecondary">
                <b>Grau de relação com o cadáver:</b>
              </Typography>
              <Typography variant="body1">
                {getLabelGrauRelacaoCadaver(dadosFormatados.reconhecedor.grauRelacao)}
              </Typography>
            </Grid>
          </Grid>
        </ModalConfirmacao>
      )}
    </div>
  )
}

Form.propTypes = {
  cadaver: PropTypes.object.isRequired,
}
